import { ROLES } from "./constants";
import { decryptPayload } from "./utils";

export const ELDORADO_FROM_WHERE = "eldorado_site";
export const ELDORADO_SOURCE = "EL Dorado";
export const ELDORADO_SIGNUP_STORAGE_KEY = "eldorado_signup";
export const ELDORADO_COUNTY_NAME = "El Dorado";
export const ELDORADO_COUNTY_ONLY_MESSAGE =
  "You can only add Eldorado County properties for Complimentary Enhanced Monitoring.";
export const ELDORADO_COMPLIMENTARY_ENHANCED_NOTE =
  "Complimentary Enhanced Monitoring is only valid for El Dorado County properties.";

/**
 * County sites whose visitors sign up for complimentary Enhanced Monitoring.
 * El Dorado was the first; the others reuse its flow, told apart by from_where
 * (keep in sync with epbe ElDoradoCountyEnrollmentService::COUNTY_SITES).
 */
export const COMPLIMENTARY_COUNTY_SITES = [
  {
    fromWhere: ELDORADO_FROM_WHERE,
    source: ELDORADO_SOURCE,
    countyName: ELDORADO_COUNTY_NAME,
    countyCodes: ["eldorado", "eldocountyca"],
    countyOnlyMessage: ELDORADO_COUNTY_ONLY_MESSAGE,
    enhancedNote: ELDORADO_COMPLIMENTARY_ENHANCED_NOTE,
  },
  {
    fromWhere: "gaston_site",
    source: "Gaston",
    countyName: "Gaston",
    countyCodes: ["gastoncountync", "gastonnc"],
    countyOnlyMessage:
      "You can only add Gaston County properties for Complimentary Enhanced Monitoring.",
    enhancedNote:
      "Complimentary Enhanced Monitoring is only valid for Gaston County properties.",
    // Owners choose free Enhanced or paid Complete Protection before signup. Both
    // carry this site's fromWhere; the account's plan (product_tier) tells them apart.
    offerPaidProtection: true,
    // State recording fee (deposit) shown on the plan choice for this county's
    // state (NC). The plan choice comes before signup, so it can't read the admin
    // Statewise Recording Fees; keep this in step with the NC entry there.
    recordingFee: "$85.00",
  },
];

const ELDORADO_SITE = COMPLIMENTARY_COUNTY_SITES[0];

// Unknown site: callers read its fields, so it is blank rather than null.
const NO_COUNTY_SITE = {
  fromWhere: "",
  source: "",
  countyName: "",
  countyCodes: [],
  countyOnlyMessage: "",
  enhancedNote: "",
};

/**
 * Free site of the county a "Do Not have a Token" signup started on, as sent by
 * county/details (free_site). Kept so later signup steps can resolve its from_where.
 */
export const COUNTY_FREE_SITE_KEY = "county_free_site";

export const mapFreeSite = (freeSite) =>
  freeSite?.from_where
    ? {
        fromWhere: String(freeSite.from_where).toLowerCase(),
        source: freeSite.county_name,
        countyName: freeSite.county_name,
        countyCodes: freeSite.county_codes || [],
        countyOnlyMessage: freeSite.county_only_message,
        enhancedNote: freeSite.enhanced_note,
        offerPaidProtection: Boolean(freeSite.offer_paid_protection),
        recordingFee: freeSite.recording_fee || null,
      }
    : null;

export const saveCountyFreeSite = (site) => {
  if (typeof window === "undefined" || !site?.fromWhere) {
    return;
  }
  localStorage.setItem(COUNTY_FREE_SITE_KEY, JSON.stringify(site));
};

const getStoredCountyFreeSite = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return JSON.parse(localStorage.getItem(COUNTY_FREE_SITE_KEY) || "null");
  } catch {
    return null;
  }
};

export const findComplimentaryCountySite = (fromWhere) => {
  const key = String(fromWhere || "")
    .trim()
    .toLowerCase();
  if (!key) {
    return null;
  }
  const stored = getStoredCountyFreeSite();
  if (stored?.fromWhere === key) {
    return stored;
  }
  return (
    COMPLIMENTARY_COUNTY_SITES.find((site) => site.fromWhere === key) || null
  );
};

/**
 * A user who signed up on a county's free site carries that site's from_where
 * (e.g. benton_site) and the county record, which is enough to rebuild the site
 * in a browser that never stored it (admin workspace, resume, a later login).
 * Wording matches epbe CountySites::freeSitePayload.
 */
const siteFromCountyDetails = (fromWhere, countyDetails) => {
  const key = String(fromWhere || "")
    .trim()
    .toLowerCase();
  const countyName = String(countyDetails?.county_name || "").trim();
  if (!key.endsWith("_site") || key === "affiliate_site" || !countyName) {
    return null;
  }
  return {
    fromWhere: key,
    source: countyName,
    countyName,
    countyCodes: [countyDetails?.county_domain_name]
      .filter(Boolean)
      .map((code) => String(code).toLowerCase()),
    countyOnlyMessage: `You can only add ${countyName} County properties for Complimentary Enhanced Monitoring.`,
    enhancedNote: `Complimentary Enhanced Monitoring is only valid for ${countyName} County properties.`,
  };
};

/** Site behind a user/profile/resume object, from its from_where and county. */
const findComplimentaryCountySiteForUser = (source = {}) =>
  findComplimentaryCountySite(source?.from_where) ||
  findComplimentaryCountySite(source?.user?.from_where) ||
  siteFromCountyDetails(source?.from_where, source?.county_details) ||
  siteFromCountyDetails(
    source?.user?.from_where,
    source?.user?.county_details || source?.county_details,
  );

const getStoredFromWhere = () =>
  typeof window === "undefined" ? null : localStorage.getItem("from_where");

/**
 * Site behind a from_where string, a user/profile object, or (no argument) the
 * signup in progress. An El Dorado county record still means El Dorado; anything
 * else unknown is a blank site, so one county never shows another's wording.
 */
export const getComplimentaryCountySite = (source) => {
  if (source === undefined || source === null) {
    return (
      findComplimentaryCountySite(getEldoradoSignupHandoff()?.from_where) ||
      findComplimentaryCountySite(getStoredFromWhere()) ||
      NO_COUNTY_SITE
    );
  }
  if (typeof source === "string") {
    return findComplimentaryCountySite(source) || NO_COUNTY_SITE;
  }
  if (
    isEldoradoCountyDomainName(source?.county_details?.county_domain_name) ||
    isEldoradoCountyDomainName(source?.user?.county_details?.county_domain_name)
  ) {
    return findComplimentaryCountySiteForUser(source) || ELDORADO_SITE;
  }
  return findComplimentaryCountySiteForUser(source) || NO_COUNTY_SITE;
};

/**
 * Paid signup from a county site (it carries the same from_where as a free one):
 * the account is on Complete Protection. Used to keep resume from switching it
 * into the complimentary signup.
 */
export const isCompleteProtectionAccount = (data = {}) =>
  (data?.product_tier ??
    data?.user?.product_tier ??
    data?.category ??
    data?.user?.category) === "complete_protection";

export const splitPersonName = (fullName = "") => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" ") || "",
  };
};

/** A complimentary county-site signup (El Dorado, Gaston…) is in progress. */
export const isEldoradoSiteSignup = () =>
  Boolean(findComplimentaryCountySite(getStoredFromWhere()));

export const normalizeCountyNameForCompare = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+county\s*$/i, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** County name belongs to the site's county ("El Dorado", "EL Dorado County"…). */
export const isEldoradoCountyName = (
  name,
  site = getComplimentaryCountySite(),
) => {
  const normalized = normalizeCountyNameForCompare(name);
  const countyName = normalizeCountyNameForCompare(site?.countyName);
  if (!normalized || !countyName) {
    return false;
  }
  return (
    normalized.includes(countyName) ||
    normalized.replace(/\s/g, "").includes(countyName.replace(/\s/g, "")) ||
    normalized === normalizeCountyNameForCompare(site.source)
  );
};

export const isEldoradoCountySelection = (
  propertyLike = {},
  site = getComplimentaryCountySite(),
) => {
  const handoff = getEldoradoSignupHandoff();
  const allowedCountyId =
    handoff?.from_where === site.fromWhere ? handoff?.county_id : null;
  const selectedCountyId =
    propertyLike?.county_code ??
    propertyLike?.county_id ??
    propertyLike?.county?.id ??
    null;

  if (
    allowedCountyId != null &&
    selectedCountyId != null &&
    String(allowedCountyId) === String(selectedCountyId)
  ) {
    return true;
  }

  const countyCode = String(
    propertyLike?.county_code ?? propertyLike?.county?.county_code ?? "",
  )
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (
    countyCode &&
    site.countyCodes.some(
      (code) => countyCode === code || countyCode.includes(code),
    )
  ) {
    return true;
  }

  return isEldoradoCountyName(
    propertyLike?.county ||
      propertyLike?.county_name ||
      propertyLike?.county?.county_name ||
      propertyLike?.county?.name,
    site,
  );
};

/** Returns an error message when a county-site signup must block this county; otherwise null. */
export const getEldoradoCountyRestrictionMessage = (propertyLike = {}) => {
  if (!isEldoradoSiteSignup()) {
    return null;
  }
  const site = getComplimentaryCountySite();
  if (isEldoradoCountySelection(propertyLike, site)) {
    return null;
  }
  return site.countyOnlyMessage;
};

/** True when county_details.county_domain_name identifies El Dorado. */
export const isEldoradoCountyDomainName = (domainName) =>
  String(domainName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "") === "eldorado";

/**
 * El Dorado complimentary user: from_where = eldorado_site OR
 * county_details.county_domain_name = eldorado (admin users table / resume).
 */
export const isEldoradoFromWhere = (source) => {
  if (source === undefined || source === null) {
    return isEldoradoSiteSignup();
  }
  if (typeof source === "string") {
    return Boolean(findComplimentaryCountySite(source));
  }
  return (
    Boolean(findComplimentaryCountySiteForUser(source)) ||
    isEldoradoCountyDomainName(source?.county_details?.county_domain_name) ||
    isEldoradoCountyDomainName(source?.user?.county_details?.county_domain_name)
  );
};

/** Pricing note for the user's county site (El Dorado by default). */
export const getComplimentaryEnhancedNote = (source) =>
  getComplimentaryCountySite(source).enhancedNote;

/** Admin workspace: complimentary vs paid Enhanced chosen on pricing screen. */
export const ELDORADO_ADMIN_ENHANCED_BILLING_KEY =
  "eldorado_admin_enhanced_billing";

export const setEldoradoAdminEnhancedBillingChoice = (mode) => {
  if (mode !== "complimentary" && mode !== "regular") {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(ELDORADO_ADMIN_ENHANCED_BILLING_KEY, mode);
};

export const getEldoradoAdminEnhancedBillingChoice = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem(ELDORADO_ADMIN_ENHANCED_BILLING_KEY);
};

export const clearEldoradoAdminEnhancedBillingChoice = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(ELDORADO_ADMIN_ENHANCED_BILLING_KEY);
};

export const isAdminEldoradoComplimentaryEnhancedSelected = () =>
  getEldoradoAdminEnhancedBillingChoice() === "complimentary";

export const isSuperOrInternalAdminRoles = (roles) => {
  if (!roles) {
    return false;
  }
  if (Array.isArray(roles)) {
    return roles.includes(ROLES.admin) || roles.includes(ROLES.subAdmin);
  }
  const value = String(roles);
  return value.includes(ROLES.admin) || value.includes(ROLES.subAdmin);
};

/** Super Admin or internal Admin (not service provider / customer). */
export const isLoggedInSuperOrInternalAdmin = (profileOrRoles = null) => {
  const fromArg = isSuperOrInternalAdminRoles(
    profileOrRoles?.data?.role ?? profileOrRoles?.role ?? profileOrRoles,
  );
  if (fromArg) {
    return true;
  }
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return isSuperOrInternalAdminRoles(
      decryptPayload(localStorage.getItem("roles")),
    );
  } catch {
    return false;
  }
};

export const complimentaryEnhancedFreeFlagPayload = (
  isAdminComplimentary = false,
  { isAffiliate = false, isCountyInvestor = false } = {},
) => {
  // Admin resume for a county investor uses the county flag, not admin_free_enhanced.
  if (isAdminComplimentary && isCountyInvestor) {
    return { county_investor_free_enhanced: true };
  }
  if (isAdminComplimentary) {
    return { admin_free_enhanced: true };
  }
  // Customer/user signup only — never use this on admin-driven enroll.
  if (isAffiliate) {
    return { is_affiliate_user: true };
  }
  if (isCountyInvestor) {
    return { county_investor_free_enhanced: true };
  }
  return { csi_free_enhanced: true };
};

/**
 * Payload for POST payment-enhanced-monitoring after admin complimentary
 * Enhanced enrollment. Prefer ids from add-property-in-report (or equivalent).
 */
export const buildComplimentaryEnhancedMonitoringPayload = (
  reportResponse = {},
  fallbackUserId = null,
  { isAdminComplimentary = true } = {},
) => {
  const data = reportResponse?.data || {};
  const nested =
    data?.data && typeof data.data === "object" && !Array.isArray(data.data)
      ? data.data
      : {};
  const userData =
    data?.user_data && typeof data.user_data === "object" ? data.user_data : {};

  const userId = Number(
    data.user_id ??
      nested.user_id ??
      userData.user_id ??
      data.owner_id ??
      fallbackUserId,
  );

  const reportRaw =
    data.risk_report_id ??
    nested.risk_report_id ??
    userData.risk_report_id ??
    data.report_id ??
    nested.report_id ??
    data.id ??
    nested.id;

  const riskReportIds = (Array.isArray(reportRaw) ? reportRaw : [reportRaw])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!Number.isFinite(userId) || userId <= 0 || !riskReportIds.length) {
    return null;
  }

  return {
    user_id: userId,
    risk_report_id: riskReportIds,
    ...complimentaryEnhancedFreeFlagPayload(isAdminComplimentary),
  };
};

/** Admin /properties workspace add-property or add-subscription for El Dorado users. */
export const isAdminEldoradoWorkspaceUser = (userLike = {}) =>
  isEldoradoFromWhere(userLike);

/**
 * Logged-in /properties Add Property: the profile signed up through a county site
 * (free or paid, e.g. eldorado_site / gaston_site) and the selected property is
 * in that county.
 */
export const isEldoradoComplimentaryEnhancedEligible = (
  profileOrSource = null,
  propertyLike = {},
) =>
  isEldoradoFromWhere(profileOrSource) &&
  isEldoradoCountySelection(
    propertyLike,
    getComplimentaryCountySite(profileOrSource),
  );

export const normalizeSignupEmail = (email = "") =>
  String(email || "")
    .trim()
    .toLowerCase();

export const isSameEldoradoSignupEmail = (email) => {
  const handoff = getEldoradoSignupHandoff();
  if (!handoff?.email) {
    return false;
  }
  return normalizeSignupEmail(handoff.email) === normalizeSignupEmail(email);
};

export const getEldoradoSignupHandoff = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(ELDORADO_SIGNUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearEldoradoSignupHandoff = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(ELDORADO_SIGNUP_STORAGE_KEY);
  if (isEldoradoSiteSignup()) {
    localStorage.removeItem("from_where");
  }
  localStorage.removeItem(COUNTY_FREE_SITE_KEY);
};

/**
 * Keep El Dorado identity when the user edits email/phone on create-account.
 * Updates handoff contact fields only; county / from_where / risk report stay.
 * @returns {boolean} true when handoff contact was synced
 */
export const syncEldoradoSignupHandoffContact = ({ email, phone } = {}) => {
  if (typeof window === "undefined") {
    return false;
  }
  if (!isEldoradoSiteSignup() && !getEldoradoSignupHandoff()) {
    return false;
  }
  const handoff = getEldoradoSignupHandoff();
  if (!handoff) {
    return false;
  }

  const site = getComplimentaryCountySite();
  const payload = {
    ...handoff,
    from_where: handoff.from_where || site.fromWhere,
    source: handoff.source || site.source,
    email: email != null && email !== "" ? email : handoff.email || "",
    phone: phone != null && phone !== "" ? phone : handoff.phone || "",
  };

  localStorage.setItem("from_where", payload.from_where);
  localStorage.setItem(ELDORADO_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
  return true;
};

/**
 * Paid signup from a county site: from_where / source / county_id for send-otp.
 * Kept apart from the free handoff (and out of localStorage from_where) so the
 * wizard runs as Complete Protection; the router drops it once the visitor
 * leaves create-account.
 */
export const COUNTY_SITE_PAID_SIGNUP_STORAGE_KEY = "county_site_paid_signup";

export const saveCountySitePaidSignup = ({ riskReport, site } = {}) => {
  if (typeof window === "undefined" || !site?.offerPaidProtection) {
    return null;
  }
  const payload = {
    from_where: site.fromWhere,
    source: site.source,
    county_id: riskReport?.county_id ?? riskReport?.county?.id ?? null,
  };
  localStorage.setItem(
    COUNTY_SITE_PAID_SIGNUP_STORAGE_KEY,
    JSON.stringify(payload),
  );
  return payload;
};

export const getCountySitePaidSignup = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(COUNTY_SITE_PAID_SIGNUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearCountySitePaidSignup = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(COUNTY_SITE_PAID_SIGNUP_STORAGE_KEY);
};

export const saveEldoradoSignupHandoff = ({
  formData,
  riskReport,
  site = ELDORADO_SITE,
} = {}) => {
  clearCountySitePaidSignup();
  const { first_name, last_name } = splitPersonName(formData?.name);
  const payload = {
    from_where: site.fromWhere,
    source: site.source,
    email: formData?.email || "",
    phone: formData?.phone || formData?.phone1 || "",
    first_name,
    last_name,
    from_risk_report_id: riskReport?.id ?? riskReport?.risk_report_id ?? null,
    county_id: riskReport?.county_id ?? riskReport?.county?.id ?? null,
  };

  localStorage.setItem("from_where", site.fromWhere);
  localStorage.setItem(ELDORADO_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
  return payload;
};

export const withEldoradoFromWhere = (data = {}, source) => {
  if (!isEldoradoFromWhere(source)) {
    return data;
  }
  return {
    ...data,
    from_where: getComplimentaryCountySite(source).fromWhere,
  };
};

export const persistEldoradoSignupFromResume = (data = {}) => {
  if (typeof window === "undefined") {
    return false;
  }
  // Only restore handoff when the user/account explicitly has from_where.
  // Do not invent El Dorado signup from county_domain_name alone (e.g. a
  // changed-email create-account whose send-otp still had leftover county_id).
  const site = findComplimentaryCountySiteForUser(data);
  // A paid signup from the same site carries the same from_where; it resumes as
  // the normal Complete Protection signup, not the complimentary one.
  if (!site || isCompleteProtectionAccount(data)) {
    return false;
  }
  // Later steps look the site up by from_where alone.
  if (!COMPLIMENTARY_COUNTY_SITES.includes(site)) {
    saveCountyFreeSite(site);
  }

  const existing = getEldoradoSignupHandoff() || {};
  const userDetails = data?.user_signup_details || {};
  const payload = {
    from_where: site.fromWhere,
    source: data?.source || existing.source || site.source,
    email: data?.email || userDetails?.email || existing.email || "",
    phone: data?.phone || userDetails?.phone || existing.phone || "",
    first_name:
      data?.first_name || userDetails?.first_name || existing.first_name || "",
    last_name:
      data?.last_name || userDetails?.last_name || existing.last_name || "",
    from_risk_report_id:
      data?.risk_report_id ??
      data?.from_risk_report_id ??
      data?.report_details?.id ??
      data?.report_details?.risk_report_id ??
      existing.from_risk_report_id ??
      null,
    county_id:
      data?.county_id ?? data?.county_details?.id ?? existing.county_id ?? null,
  };

  localStorage.setItem("from_where", site.fromWhere);
  localStorage.setItem(ELDORADO_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
  return true;
};

const ELDORADO_OWNER_VERIFICATION_STEPS = new Set([
  "VERIFY_EMAIL_OTP",
  "GENERATE_QUESTIONS",
  "GET_MAILING_ADDRESS",
  "VERIFY_ANSWERS",
  "SKIP_VERIFY_ANSWERS",
]);

export const applyEldoradoResumeStep = (jsonLastStep = {}) => {
  if (!isEldoradoSiteSignup()) {
    return null;
  }

  const stepKey = jsonLastStep?.step_key || "";

  if (stepKey === "SEND_OTP") {
    localStorage.setItem("currentStep", "0");
    localStorage.removeItem("emailScreen");
    return 0;
  }

  if (stepKey === "VERIFY_PHONE_OTP") {
    localStorage.setItem("currentStep", "0");
    localStorage.setItem("emailScreen", "1");
    return 0;
  }

  if (ELDORADO_OWNER_VERIFICATION_STEPS.has(stepKey)) {
    localStorage.setItem("currentStep", "2");
    localStorage.setItem("isOwnerVerified", "1");
    localStorage.setItem("OwnerVerifiedScreen", "1");
    localStorage.setItem("button", "propertyList");
    localStorage.removeItem("emailScreen");
    return 2;
  }

  return null;
};

export const isPropertyAlreadyMonitored = (riskManagementData) => {
  const flag =
    riskManagementData?.data?.property_already_added_by_someone ??
    riskManagementData?.property_already_added_by_someone;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
};

// The parcel is held by another account's Complete Protection (not just monitoring started).
export const isPropertyOnAnotherAccount = (riskManagementData) => {
  const flag =
    riskManagementData?.data?.property_on_another_account ??
    riskManagementData?.property_on_another_account;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
};
